import { formatDate, TimeFormats } from "@theprelude/core";
import {
  Button,
  Checkbox,
  ExternalLinkIcon,
  NetworkIcon,
  TrendingIcon,
  Truncate,
} from "@theprelude/ds";
import { ControlCodes, PartnerAdvisory } from "@theprelude/sdk";
import { useState } from "react";
import { AdvisoryDeepLink } from "../deep-links";
import { Flag } from "../flag/flag";
import { Highlight } from "../highlight";
import { Tags } from "../tags";

export function Advisory({
  advisory,
  disabled,
  loading,
  searchTerm,
  onGenerate,
}: {
  advisory: PartnerAdvisory;
  disabled?: boolean;
  loading?: boolean;
  searchTerm: string;
  onGenerate: (forceAI: boolean) => void;
}) {
  const [forceAI, setForceAI] = useState(false);
  const [canGenerate, setCanGenerate] = useState(false);
  const slug = advisory.slug.toUpperCase();

  return (
    <div className="relative flex w-full flex-col gap-6 border border-stroke-line bg-surface-background p-4">
      <div className="flex flex-col gap-2">
        <div className="flex flex-grow gap-2">
          <div className="inline-flex flex-grow items-center gap-2">
            <span>CrowdStrike</span>
            <TrendingIcon className="fill-status-red" />
            <span className="flex max-w-56 items-center gap-1">
              <Truncate text={slug}>
                <Highlight text={slug} highlight={searchTerm} />
              </Truncate>
              <AdvisoryDeepLink
                control={ControlCodes.CROWDSTRIKE}
                slug={advisory.slug}
                className="text-secondary-foreground hover:text-primary-foreground"
              >
                <ExternalLinkIcon className="size-4" />
              </AdvisoryDeepLink>
            </span>
          </div>
          <span className="text-secondary-foreground">
            {formatDate(new Date(advisory.created), TimeFormats.DATE_FIRST)}
          </span>
        </div>
        <div className="text-lg">
          <strong>
            <Highlight text={advisory.name} highlight={searchTerm} />
          </strong>
        </div>
        <div>
          <Highlight text={advisory.description} highlight={searchTerm} />
        </div>
        <AdvisoryDeepLink
          control={ControlCodes.CROWDSTRIKE}
          slug={advisory.slug}
        >
          <span className="text-xs underline">See full details</span>
        </AdvisoryDeepLink>
      </div>
      {canGenerate ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={!forceAI}
              onCheckedChange={() => {
                setForceAI(!forceAI);
              }}
            />
            <span>
              Use Prelude Verified Tests instead of AI-generated tests when
              possible.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              disabled={disabled}
              loading={loading}
              intent="primary"
              onClick={() => onGenerate(forceAI)}
            >
              Continue
            </Button>
            <Button
              disabled={disabled}
              intent="secondary"
              onClick={() => setCanGenerate(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Tags
            tags={advisory.tags.sort((a, b) => a.localeCompare(b))}
            limit={2}
          />
          <Flag features={["threat_intel"]}>
            <Button
              disabled={disabled}
              loading={loading}
              icon={<NetworkIcon className="text-secondary-foreground" />}
              intent="secondary"
              onClick={() => setCanGenerate(true)}
            >
              Generate Tests and Detections
            </Button>
          </Flag>
        </div>
      )}
    </div>
  );
}
